import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';
import { formatInstant, formatUtc, addUtc, parseCalendarDate } from '../../../../utilities/dateTime';
import { parseUtcTimestamp } from '../../../../utilities/utcDateTime';
import { useNavigate } from 'react-router-dom';
import {
  MoreVertical,
  CalendarClock,
  Edit2,
  Trash2,
  Calendar,
  Clock,
  AlignLeft,
  MapPin,
  Video,
  Users,
  FileText,
  ArrowUpRight,
  Loader2,
  CheckCircle2,
  XCircle,
  HelpCircle,
  MinusCircle,
} from 'lucide-react';

import {
  AcceptRejectMeetingInvitationAsync,
  deleteEvent,
  deleteTimeOffRequest,
  getAllEmployees,
} from '../../../../services/calendarService/EventService';
import { useAuth } from '../../../../firebase/AuthProvider';
import { AllEvent, AllUsers, AttendeeResponse, InternalInvitationResponseAction } from '../../../../interfaces/identity/calendar/Events';
import { GetExistingOrCretateMeetingAsync } from '../../../../services/videoService/VideoService';
import { LobbyState } from '../../../../interfaces/videocallDtos/videoCall';
import { showConfirmationDialog } from '../../../modal/ConfimationModal/Confimation';
import { encodeLobbyStateToToken } from '../../../../utilities/lobbyStateToken';
import { getTaskById } from '../../../../services/taskServices/TaskService';

import Loader from '../../loader/Loader';
import EditEvent from './EditEvent';
import ScopeModal from './ScopeModal';
import ModalMeet from '../ModalMeet';
import RescheduleMeetingModal from '../../tasks/Modals/RescheduleMeetingModal';
import AvatarImage from '../../../ui/Avatar/AvatarImage';
import UserProfileTooltip from '../../../ui/Tooltip/UserProfileTooltip';
import ParticipantProfileTooltip from '../../../ui/Tooltip/ParticipantProfileTooltip';
import Tooltip from '../../../ui/Tooltip/Tooltip';
import {
  formatLeaveLocalTimeRange,
  formatLeaveTypeLabel,
  leaveLocalDate,
} from '../../../../utilities/leaveDateTime';
import { canChangeInvitationResponse, getAttendeeInviteStatus, getResponseActionForStatus, getRsvpPresentation } from '../../../../utilities/calendarRsvp';
import './ViewEvent.css';
import { dispatchMeetingInvitationStateChanged } from '../../../../utilities/notifications/meetingInvitationEvents';

interface EventData {
  title: string;
  startTime: string;
  endTime: string;
  description: string;
  location: string;
  meetingDetails: string;
  ownerUserID: string;
  attendees: AttendeeResponse[];
  taskId: number;
  taskTitle: string;
  online: boolean;
  isSprout: boolean;
  recurrenceRule?: string;
  parentEventId?: string;
  time_zone?: string;
}

interface ViewEventProps {
  event: AllEvent | null;
  onClose: () => void;
  refreshAllEvents?: () => void;
  displayTimeZone?: string;
}

export default function ViewEvent({ event, onClose, refreshAllEvents, displayTimeZone }: ViewEventProps) {
  const { currentUser, currentUserInfo } = useAuth();
  const token = (currentUser as any)?.accessToken;
  const navigate = useNavigate();

  const modalContentRef = useRef<HTMLDivElement>(null);
  const actionsDropdownRef = useRef<HTMLDivElement>(null);

  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [invitationResponseAction, setInvitationResponseAction] = useState<InternalInvitationResponseAction | null>(null);

  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [showDeleteScopeModal, setShowDeleteScopeModal] = useState(false);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const [employees, setEmployees] = useState<AllUsers[]>([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [linkedTask, setLinkedTask] = useState<Awaited<ReturnType<typeof getTaskById>>>(null);
  const [isLinkedTaskLoading, setIsLinkedTaskLoading] = useState(false);
  const [linkedTaskLoadFailed, setLinkedTaskLoadFailed] = useState(false);

  const [formData, setFormData] = useState<EventData>({
    title: '', startTime: '', endTime: '', description: '', location: '',
    meetingDetails: '', ownerUserID: '', attendees: [],
    taskId: 0, taskTitle: '', online: true, isSprout: true,
  });

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close this modal on Escape, but do not also close it when a nested
  // edit/reschedule/scope modal is currently handling the user's action.
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (
        e.key === 'Escape' &&
        !showDeleteScopeModal &&
        !isEditModalOpen &&
        !showRescheduleModal
      ) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose, showDeleteScopeModal, isEditModalOpen, showRescheduleModal]);

  // Click outside handlers
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (showActions && actionsDropdownRef.current && !actionsDropdownRef.current.contains(e.target as Node)) {
        setShowActions(false);
      }
      if (!showDeleteScopeModal && !isEditModalOpen && !showRescheduleModal) {
        if (modalContentRef.current && !modalContentRef.current.contains(e.target as Node)) {
          const target = e.target as HTMLElement;
          if (!target.closest('.Toastify') && !target.closest('.modal-portal')) {
            onClose();
          }
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showDeleteScopeModal, isEditModalOpen, showRescheduleModal, showActions]);

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!currentUser || !token) return;
      try {
        const response = await getAllEmployees(token);
        if (response) setEmployees(response);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };
    fetchInitialData();
  }, [currentUser, token]);

  useEffect(() => {
    if (!event?.taskId || !token || (event as any).isTimeOff) {
      setLinkedTask(null);
      setLinkedTaskLoadFailed(false);
      setIsLinkedTaskLoading(false);
      return;
    }

    let cancelled = false;

    const hydrateLinkedTask = async () => {
      setIsLinkedTaskLoading(true);
      setLinkedTaskLoadFailed(false);

      try {
        const task = await getTaskById(String(event.taskId), token);

        if (cancelled) return;

        if (task) {
          setLinkedTask(task);
        } else {
          setLinkedTask(null);
          setLinkedTaskLoadFailed(true);
        }
      } catch (error) {
        if (cancelled) return;

        console.error('Error loading linked task for calendar event:', error);
        setLinkedTask(null);
        setLinkedTaskLoadFailed(true);
      } finally {
        if (!cancelled) {
          setIsLinkedTaskLoading(false);
        }
      }
    };

    void hydrateLinkedTask();

    return () => {
      cancelled = true;
    };
  }, [event?.taskId, token, event]);

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '', startTime: event.startTime, endTime: event.endTime,
        description: event.description || '', location: event.location, meetingDetails: event.meetingDetails,
        ownerUserID: event.ownerUserID || '',
        attendees: event.attendees || [],
        taskId: event.taskId || 0,
        taskTitle: event.taskTitle || '', online: event.online, isSprout: event.isSprout,
        recurrenceRule: event.recurrenceRule, parentEventId: event.parentEventId, time_zone: event.time_zone,
      });
    }
  }, [event]);

  const handleDelete = async () => {
    if (!event || !token) return;
    setShowActions(false);

    // --- NEW: Intercept Time Off Cancellations ---
    if ((event as any).isTimeOff) {
      const isPending = (event as any).timeOffStatus?.toUpperCase() === "PENDING";
      const isConfirmed = await showConfirmationDialog(
        isPending ? "Delete Pending Time Off" : "Cancel Time Off",
        isPending
          ? "Are you sure you want to delete this pending time-off request?"
          : "Are you sure you want to request cancellation of this approved time off?"
      );
      if (isConfirmed) {
        setLoading(true);
        try {
          const res = await deleteTimeOffRequest((event as any).timeOffId, token);
          if (res.success) {
            // This will elegantly show "Cancellation request submitted..." OR "Time off deleted successfully."
            toast.success(res.message);
            if (refreshAllEvents) refreshAllEvents();
            onClose();
          } else {
            toast.error(res.message || 'Failed to cancel time off.');
          }
        } catch (error) {
          toast.error('An error occurred.');
        } finally {
          setLoading(false);
        }
      }
      return;
    }
    // ----------------------------------------------

    // Existing standard event deletion logic
    if (event.recurrenceRule || event.parentEventId) {
      setShowDeleteScopeModal(true);
    } else {
      const isConfirmed = await showConfirmationDialog("Delete Event", "Are you sure you want to delete this event?");
      if (isConfirmed) await executeDelete('single');
    }
  };

  const executeDelete = async (scope: 'single' | 'future') => {
    if (!event || !token) return;
    setLoading(true);
    try {
      const success = await deleteEvent(event.id, token, scope);
      if (success) {
        toast.success('Event deleted successfully!');
        if (refreshAllEvents) refreshAllEvents();
        onClose();
      } else {
        toast.error('Failed to delete the event.');
      }
    } catch (error) {
      toast.error('An error occurred.');
    } finally {
      setLoading(false);
      setShowDeleteScopeModal(false);
    }
  };

  const handleClickJoin = (eventId: string, disabled: boolean) => {
    if (disabled) {
      toast.info('Joining opens 30 min before start.');
      return;
    }
    setLoading(true);
    GetExistingOrCretateMeetingAsync(token, eventId)
      .then((res) => {
        if (res.success) {
          const states: LobbyState = { calenderEventId: eventId, isPublisher: res.data.isPublisher, isOneToOneCall: false };
          navigate(`/lobby/${res.data.roomId}?state=${encodeURIComponent(encodeLobbyStateToToken(states))}`);
        } else toast.error(res.message);
      })
      .catch((err) => toast.error(err.message))
      .finally(() => setLoading(false));
  };

  const updateMeetingInvitationResponse = async (action: InternalInvitationResponseAction) => {
    if (!currentUser || !event?.id || !token || invitationResponseAction) return;

    if (action === 'DECLINE') {
      const isConfirm = await showConfirmationDialog(
        "Decline Meeting",
        "Are you sure you cannot attend this meeting? It will be removed from your calendar."
      );
      if (!isConfirm) return;
    }

    setLoading(true);
    setInvitationResponseAction(action);
    try {
      const res = await AcceptRejectMeetingInvitationAsync(event.id, action, token);
      if (res.success) {
        const nextStatus: AttendeeResponse['inviteStatus'] = action === 'ACCEPT'
          ? 'ACCEPTED'
          : action === 'TENTATIVE'
            ? 'TENTATIVE'
            : 'DECLINED';
        setFormData((previous) => ({
          ...previous,
          attendees: previous.attendees.map((attendee) =>
            attendee.ref === currentUser.uid
              ? { ...attendee, inviteStatus: nextStatus, respondedAt: new Date().toISOString() }
              : attendee
          ),
        }));
        if (refreshAllEvents) refreshAllEvents();
        dispatchMeetingInvitationStateChanged();
        toast.success(res.message);
        if (action === 'DECLINE') onClose();
      } else {
        toast.error(res.message);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Unable to update the invitation.');
    } finally {
      setLoading(false);
      setInvitationResponseAction(null);
    }
  };

  const isOwnerOrParticipantOfEvent = useCallback((): boolean => {
    if (!currentUser || !event) return false;
    const isParticipant = event.attendees?.some(a => a.ref === currentUser.uid);
    return event.ownerUserID === currentUser.uid || isParticipant;
  }, [event, currentUser]);

  const getInitials = (name: string) => {
    if (!name) return "?";
    const words = name.trim().split(/\s+/);
    return words.length === 1 ? words[0].charAt(0).toUpperCase() : (words[0][0] + words[1][0]).toUpperCase();
  };

  if (!event) return null;

  const start = parseUtcTimestamp(event.startTime)!;
  const end = parseUtcTimestamp(event.endTime)!;
  const preJoinStart = addUtc(start, -30, 'minutes');
  const isWithinPrejoinWindow = currentTime >= preJoinStart && currentTime <= end;
  const canJoin = isWithinPrejoinWindow;

  const description = formData.description || "No description provided.";
  const displayedText = !isExpanded && description.length > 140 ? description.slice(0, 140) + "..." : description;

  const isTimeOff = Boolean((event as any).isTimeOff);
  const isPendingTimeOff = isTimeOff && (event as any).timeOffStatus?.toUpperCase() === "PENDING";
  const isPartialDayTimeOff = isTimeOff && (event as any).requestType === "PARTIAL_DAY";
  const leaveStart = isTimeOff
    ? isPartialDayTimeOff
      ? displayTimeZone ? new Date(start) : leaveLocalDate(event.startTime)
      : parseCalendarDate(event.startTime.split("T")[0])
    : null;
  const leaveEnd = isTimeOff
    ? isPartialDayTimeOff
      ? displayTimeZone ? new Date(end) : leaveLocalDate(event.endTime)
      : parseCalendarDate(event.endTime.split("T")[0])
    : null;
  const canDeleteTimeOff = (event as any).canDeleteTimeOff !== false;
  const isFutureEvent = currentTime < start;

  // Historical calendar events remain immutable. PTO is different: a pending
  // request is still a live request and can be cancelled even when its dates
  // are already in the past. Approved PTO continues to respect the API's
  // canDelete flag and the normal future-event restriction.
  const canShowEventActions = event.ownerUserID === currentUser?.uid && (
    isTimeOff
      ? (isPendingTimeOff || (canDeleteTimeOff && isFutureEvent))
      : isFutureEvent
  );

  const attendees = formData.attendees || [];
  const visibleAttendees = attendees.slice(0, 5);
  const extraCount = attendees.length - 5;
  const currentUserInviteStatus = getAttendeeInviteStatus(
    { ownerUserID: event.ownerUserID, attendees: formData.attendees },
    currentUser?.uid
  );
  const canRespondToInvitation = canChangeInvitationResponse(currentUserInviteStatus);
  const showInvitationResponseControls =
    event?.ownerUserID !== currentUser?.uid &&
    canRespondToInvitation &&
    currentTime < start;
  const currentUserResponsePresentation = getRsvpPresentation(currentUserInviteStatus);
  const selectedResponseAction = getResponseActionForStatus(currentUserInviteStatus);

  const statusCounts = attendees.reduce<Record<string, number>>((counts, attendee) => {
    const status = attendee.inviteStatus || 'INVITED';
    counts[status] = (counts[status] || 0) + 1;
    return counts;
  }, {});

  const getStatusIcon = (status: AttendeeResponse['inviteStatus']) => {
    switch (status) {
      case 'ACCEPTED': return <CheckCircle2 className="h-3.5 w-3.5" />;
      case 'TENTATIVE': return <Clock className="h-3.5 w-3.5" />;
      case 'DECLINED': return <XCircle className="h-3.5 w-3.5" />;
      case 'CANCELLED': return <MinusCircle className="h-3.5 w-3.5" />;
      default: return <HelpCircle className="h-3.5 w-3.5" />;
    }
  };

  const linkedTaskTitle =
    linkedTask?.title ||
    formData.taskTitle ||
    (event as any)?.taskTitle ||
    (formData.taskId ? `Task #${formData.taskId}` : '');

  const linkedTaskFriendlyId =
    (linkedTask as any)?.friendlyId ??
    (event as any)?.friendlyTaskId ??
    null;

  const linkedTaskOrganizationAbbreviation =
    (linkedTask as any)?.organization?.abbreviation ??
    (linkedTask as any)?.organizationAbbreviation ??
    (event as any)?.taskOrganizationAbbreviation ??
    (event as any)?.organizationAbbreviation ??
    (currentUserInfo as any)?.organization?.abbreviation ??
    null;

  const linkedTaskReference =
    linkedTaskFriendlyId != null
      ? linkedTaskOrganizationAbbreviation
        ? `${linkedTaskOrganizationAbbreviation}-${linkedTaskFriendlyId}`
        : `#${linkedTaskFriendlyId}`
      : formData.taskId
        ? `Task #${formData.taskId}`
        : '';

  const linkedTaskPath =
    linkedTaskFriendlyId != null && linkedTaskOrganizationAbbreviation
      ? `/task/${linkedTaskOrganizationAbbreviation}-${linkedTaskFriendlyId}`
      : null;

  const handleOpenLinkedTask = () => {
    if (!linkedTaskPath) return;

    onClose();
    navigate(linkedTaskPath);
  };

  return (
    <>
      {loading && <Loader />}
      <div className="view-event-modal bg-white rounded-2xl p-6 flex flex-col relative w-full animate-in zoom-in-95 duration-200" ref={modalContentRef}>

        <div className="flex items-start justify-between gap-4 mb-5 border-b border-gray-100 pb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="view-event-header-icon w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
              <Calendar className="w-5 h-5 text-[#7E57C2] dark:text-purple-300" />
            </div>
            <h2 className="text-xl font-semibold mb-0 text-gray-900 leading-tight truncate">{formData.title}</h2>
          </div>

          {canShowEventActions && (
            <div className="relative shrink-0" ref={actionsDropdownRef}>
              <button
                onClick={() => setShowActions(!showActions)}
                className="p-2 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
              >
                <MoreVertical className="w-5 h-5" />
              </button>

              {showActions && (
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-[100]">
                  {/* Hide Edit/Reschedule for Time Off blocks */}
                  {!(event as any).isTimeOff && (
                    <>
                      <button onClick={() => { if (event.allDayStart) setEditModalOpen(true); else setShowRescheduleModal(true); setShowActions(false); }} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#7E57C2]">
                        <CalendarClock className="w-4 h-4 mr-3 text-gray-400" /> Reschedule
                      </button>
                      <button onClick={() => { setEditModalOpen(true); setShowActions(false); }} className="w-full flex items-center px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 hover:text-[#7E57C2]">
                        <Edit2 className="w-4 h-4 mr-3 text-gray-400" /> Edit Event
                      </button>
                      <div className="border-t border-gray-100 my-1"></div>
                    </>
                  )}

                  <button onClick={handleDelete} className="w-full flex items-center px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-3 text-red-500" />
                    {/* Change the text dynamically */}
                    {(event as any).isTimeOff
                      ? ((event as any).timeOffStatus?.toUpperCase() === "PENDING"
                          ? "Delete Request"
                          : "Request Cancellation")
                      : "Delete Event"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {(event as any).isTimeOff ? (
          <div className="flex flex-col gap-4 mb-6">
            <div className={`view-event-time-off-card rounded-xl border p-4 ${
              (event as any).timeOffStatus?.toUpperCase() === "PENDING"
                ? "view-event-time-off-card-pending border-purple-200 bg-purple-50/70"
                : "view-event-time-off-card-approved border-amber-200 bg-amber-50/70"
            }`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                    {formatLeaveTypeLabel((event as any).leaveType)}
                  </div>
                  {(event as any).ownerName && (
                    <div className="mt-1 text-xs font-semibold text-gray-600">{(event as any).ownerName}</div>
                  )}
                  <div className="mt-1 text-sm font-semibold text-gray-800">
                    {(leaveStart ? (isPartialDayTimeOff ? formatInstant(leaveStart, "EEEE, MMMM d", displayTimeZone) : formatUtc(leaveStart, "EEEE, MMMM d")) : null) || "Date unavailable"} – {(leaveEnd ? (isPartialDayTimeOff ? formatInstant(leaveEnd, "EEEE, MMMM d, yyyy", displayTimeZone) : formatUtc(leaveEnd, "EEEE, MMMM d, yyyy")) : null) || "Date unavailable"}
                  </div>
                  {isPartialDayTimeOff && (
                    <div className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#6D46B2]">
                      <Clock className="h-4 w-4" />
                      {displayTimeZone ? `${formatInstant(start, 'h:mm a xxx', displayTimeZone)} – ${formatInstant(end, 'h:mm a xxx', displayTimeZone)} · ${displayTimeZone}` : formatLeaveLocalTimeRange(event.startTime, event.endTime)}
                    </div>
                  )}
                  <div className="mt-2 text-xs font-medium text-gray-500">
                    {isPartialDayTimeOff ? "Partial day" : "Full day"}
                    {(event as any).durationMinutes > 0
                      ? ` · ${((event as any).durationMinutes / 60).toLocaleString(undefined, { maximumFractionDigits: 2 })} hours`
                      : ""}
                  </div>
                </div>
                <span className={`view-event-time-off-status shrink-0 rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wider ${
                  (event as any).timeOffStatus?.toUpperCase() === "PENDING"
                    ? "view-event-time-off-status-pending bg-purple-100 text-[#6D46B2]"
                    : "view-event-time-off-status-approved bg-amber-100 text-amber-700"
                }`}>
                  {(event as any).isCancellationPending
                    ? "Cancellation Pending"
                    : ((event as any).timeOffStatus || "Approved")}
                </span>
              </div>
            </div>

            {(event as any).description && (
              <div className="view-event-time-off-description flex items-start gap-2.5 bg-gray-50 p-3.5 rounded-xl border border-gray-100">
                <AlignLeft className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
                <div className="text-[13.5px] text-gray-600 leading-relaxed break-words">
                  {(event as any).description}
                </div>
              </div>
            )}
          </div>
        ) : (
        <div className="flex flex-col gap-4 mb-6">
          {/* NEW: Event Organizer Row */}
          <div className="view-event-organizer flex items-center gap-2.5 bg-purple-50/40 p-2.5 rounded-xl border border-purple-100/60 w-fit mb-1">
            <div className="w-6 h-6 rounded-full overflow-hidden shrink-0 shadow-sm flex justify-center items-center align-middle">
              <AvatarImage size="xxxs" userId={formData.ownerUserID} />
            </div>
            <span className="text-[13px] font-medium text-gray-600">
              Organized by <span className="font-semibold text-[#7E57C2]">
                {employees.find(e => e.userId === formData.ownerUserID)?.userName || event.ownerName || 'Unknown Organizer'}
              </span>
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="flex items-center gap-2.5">
              <Calendar className="w-4 h-4 text-gray-400" />
              <span className="text-[14px] font-medium text-gray-700">{event.allDayStart ? formatUtc(parseCalendarDate(event.allDayStart)!, 'EEEE, MMM dd') : formatInstant(parseUtcTimestamp(formData.startTime)!, 'EEEE, MMM dd', displayTimeZone)}</span>
            </div>
            <div className="flex items-center gap-2.5">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="text-[14px] font-medium text-gray-700">
                {event.allDayStart ? 'All day' : `${formatInstant(parseUtcTimestamp(formData.startTime)!, 'h:mm a', displayTimeZone)} – ${formatInstant(parseUtcTimestamp(formData.endTime)!, 'h:mm a', displayTimeZone)}`}
              </span>
            </div>
          </div>

          <div className="flex items-start gap-2.5 bg-gray-50 p-3.5 rounded-xl border border-gray-100">
            <AlignLeft className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
            <div className="text-[13.5px] text-gray-600 leading-relaxed min-w-0 break-words">
              {displayedText}
              {description.length > 140 && (
                <button type="button" onClick={() => setIsExpanded(!isExpanded)} className="ml-1.5 text-[#7E57C2] font-semibold hover:underline">
                  {isExpanded ? "Show less" : "Read more"}
                </button>
              )}
            </div>
          </div>

          {formData.taskId > 0 && (
            <button
              type="button"
              onClick={handleOpenLinkedTask}
              disabled={!linkedTaskPath || isLinkedTaskLoading}
              className={`view-event-linked-task group flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                linkedTaskPath && !isLinkedTaskLoading
                  ? "border-purple-100 bg-purple-50/40 hover:border-purple-200 hover:bg-purple-50 cursor-pointer"
                  : "border-gray-100 bg-gray-50 cursor-default"
              }`}
              aria-label={
                linkedTaskPath
                  ? `Open linked task ${linkedTaskReference}`
                  : "Linked task"
              }
            >
              <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                linkedTaskPath ? "bg-purple-100 text-[#7E57C2]" : "bg-gray-100 text-gray-400"
              }`}>
                {isLinkedTaskLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <FileText className="h-4 w-4" />
                )}
              </div>

              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-gray-400">
                  Linked Task
                </div>
                <div className="mt-0.5 truncate text-[13.5px] font-semibold text-gray-800">
                  {linkedTaskTitle}
                </div>
                <div className={`mt-0.5 truncate text-[11px] font-semibold ${
                  linkedTaskPath ? "text-[#7E57C2]" : "text-gray-400"
                }`}>
                  {isLinkedTaskLoading
                    ? "Loading task reference…"
                    : linkedTaskLoadFailed
                      ? "Task details unavailable"
                      : linkedTaskReference}
                </div>
              </div>

              {linkedTaskPath && !isLinkedTaskLoading && (
                <ArrowUpRight className="h-4 w-4 shrink-0 text-purple-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#7E57C2]" />
              )}
            </button>
          )}

          <div className="flex items-center gap-2.5">
            {formData.online ? <Video className="w-4 h-4 text-gray-400" /> : <MapPin className="w-4 h-4 text-gray-400" />}
            <div className="text-[14px] font-medium text-gray-700 min-w-0 truncate">
              {formData.location ? (
                <span>{formData.location}</span>
              ) : formData.isSprout ? (
                <span className="text-[#7E57C2]">Join via Waivera</span>
              ) : (
                <span className="text-gray-400">No location specified</span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3 mt-1">
            <Users className="w-4 h-4 text-gray-400 shrink-0" />
            {attendees.length > 0 ? (
              <div className="flex items-center gap-3">
                <div className="flex -space-x-1.5">
                  {visibleAttendees.map((attendee, idx) => {
                    if (!attendee.ref && attendee.type !== 'EXTERNAL') return null;

                    const emp = employees.find(e => e.userId === attendee.ref);

                    // FIX: AllUsers uses 'userName' for the full name instead of split fields 
                    const fullName = attendee.displayName || emp?.userName || "";

                    // Split the userName for the profile tooltips if necessary, or default to standard labels
                    const nameParts = fullName.trim().split(/\s+/);
                    const firstName = nameParts[0] || (attendee.type === 'EMPLOYEE' ? 'Employee' : 'Participant');
                    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(" ") : "";

                    const avatarNode = (
                      <div className="relative inline-block rounded-full border-2 border-white hover:z-20 hover:scale-110 transition-transform cursor-pointer shadow-sm bg-white">
                        {attendee.type === 'EXTERNAL' ? (
                          <div className="w-8 h-8 rounded-full bg-indigo-50 border border-indigo-100 flex items-center justify-center text-[10px] font-semibold text-indigo-600 uppercase">
                            {getInitials(attendee.email || '?')}
                          </div>
                        ) : (
                          <div className="w-8 h-8 rounded-full overflow-hidden">
                            <AvatarImage size="xxs" userId={attendee.ref as string} />
                          </div>
                        )}
                      </div>
                    );

                    if (attendee.type === 'EMPLOYEE') {
                      return (
                        <Tooltip
                          key={`${attendee.ref}-${idx}`}
                          position="left"
                          theme='light'
                          content={<UserProfileTooltip theme="light" member={{ id: attendee.ref as string, firstName, lastName, email: attendee.email }} />}
                        >
                          {avatarNode}
                        </Tooltip>
                      );
                    } else if (attendee.type === 'WAIVER_PARTICIPANT') {
                      return (
                        <Tooltip
                          key={`${attendee.ref}-${idx}`}
                          theme='light'
                          position="left"
                          content={<ParticipantProfileTooltip theme="light" participant={{ id: attendee.ref as string, firstName, lastName, email: attendee.email }} />}
                        >
                          {avatarNode}
                        </Tooltip>
                      );
                    } else {
                      return (
                        <Tooltip key={`ext-${idx}`} position="left"
                          theme='light'
                          content={attendee.email || 'External Guest'}>
                          {avatarNode}
                        </Tooltip>
                      );
                    }
                  })}
                  {extraCount > 0 && (
                    <div className="h-8 w-8 rounded-full ring-2 ring-white bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-600 shadow-sm">
                      +{extraCount}
                    </div>
                  )}
                </div>
                <span className="text-[13px] font-medium text-gray-500">{attendees.length} Attendee{attendees.length !== 1 ? 's' : ''}</span>
              </div>
            ) : (
              <span className="text-[13px] font-medium text-gray-400">No attendees</span>
            )}
          </div>

          {attendees.length > 0 && (
            <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-3.5">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <span className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">Invitation responses</span>
                <span className="text-[10px] font-medium text-gray-400">
                  {statusCounts.ACCEPTED || 0} accepted · {statusCounts.INVITED || 0} awaiting · {statusCounts.DECLINED || 0} declined
                </span>
              </div>
              <div className="calendar-rsvp-scrollbar max-h-48 space-y-2 overflow-y-auto pr-1">
                {attendees.map((attendee) => {
                  const employee = employees.find((item) => item.userId === attendee.ref);
                  const displayName = attendee.displayName || employee?.userName || attendee.email || 'Unknown attendee';
                  const presentation = getRsvpPresentation(attendee.inviteStatus);

                  return (
                    <div key={attendee.attendeeId} className="flex items-center gap-3 rounded-lg border border-gray-100 bg-white px-3 py-2">
                      {attendee.type === 'EXTERNAL' ? (
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-[9px] font-semibold text-indigo-600">
                          {getInitials(displayName)}
                        </div>
                      ) : (
                        <div className="h-7 w-7 shrink-0 overflow-hidden rounded-full">
                          <AvatarImage size="xxxs" userId={attendee.ref as string} />
                        </div>
                      )}
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-semibold text-gray-700">{displayName}</div>
                        <div className="text-[9px] font-medium uppercase tracking-wider text-gray-400">
                          {attendee.type === 'EXTERNAL' ? 'External guest' : attendee.type === 'WAIVER_PARTICIPANT' ? 'Participant' : 'Employee'}
                        </div>
                      </div>
                      <span className={`calendar-rsvp-badge inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-[9px] font-bold uppercase tracking-wider ${presentation.badgeClass}`}>
                        {getStatusIcon(attendee.inviteStatus)}
                        {presentation.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        )}

        <div className="mt-auto border-t border-gray-100 pt-4">
          {!(event as any).isTimeOff && isOwnerOrParticipantOfEvent() && showInvitationResponseControls && (
            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50/80 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-center gap-3">
                <div className={`calendar-rsvp-badge flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border ${currentUserResponsePresentation.badgeClass}`}>
                  {currentUserInviteStatus && currentUserInviteStatus !== 'ORGANIZER'
                    ? getStatusIcon(currentUserInviteStatus)
                    : <HelpCircle className="h-4 w-4" />}
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-gray-800">Your response</div>
                  <div className="mt-0.5 text-[11px] text-gray-500">
                    {selectedResponseAction
                      ? `${currentUserResponsePresentation.label}. You can change your response before the meeting starts.`
                      : 'Let the organizer know if you can attend.'}
                  </div>
                </div>
              </div>

              <div className="grid shrink-0 grid-cols-3 gap-2 sm:flex sm:justify-end">
                <button
                  type="button"
                  aria-pressed={selectedResponseAction === 'ACCEPT'}
                  disabled={invitationResponseAction !== null || selectedResponseAction === 'ACCEPT'}
                  onClick={() => void updateMeetingInvitationResponse('ACCEPT')}
                  className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors disabled:cursor-default disabled:opacity-100 ${selectedResponseAction === 'ACCEPT' ? 'border-emerald-700 bg-emerald-700 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-emerald-200 hover:bg-emerald-50 hover:text-emerald-700'}`}
                >
                  {invitationResponseAction === 'ACCEPT' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Going
                </button>
                <button
                  type="button"
                  aria-pressed={selectedResponseAction === 'TENTATIVE'}
                  disabled={invitationResponseAction !== null || selectedResponseAction === 'TENTATIVE'}
                  onClick={() => void updateMeetingInvitationResponse('TENTATIVE')}
                  className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors disabled:cursor-default disabled:opacity-100 ${selectedResponseAction === 'TENTATIVE' ? 'border-amber-500 bg-amber-500 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700'}`}
                >
                  {invitationResponseAction === 'TENTATIVE' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <HelpCircle className="h-3.5 w-3.5" />}
                  Maybe
                </button>
                <button
                  type="button"
                  aria-pressed={selectedResponseAction === 'DECLINE'}
                  disabled={invitationResponseAction !== null || selectedResponseAction === 'DECLINE'}
                  onClick={() => void updateMeetingInvitationResponse('DECLINE')}
                  className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border px-3 text-xs font-semibold transition-colors disabled:cursor-default disabled:opacity-100 ${selectedResponseAction === 'DECLINE' ? 'border-red-600 bg-red-600 text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-red-200 hover:bg-red-50 hover:text-red-700'}`}
                >
                  {invitationResponseAction === 'DECLINE' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XCircle className="h-3.5 w-3.5" />}
                  Can’t go
                </button>
              </div>
            </div>
          )}

          <div className="flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-10 items-center justify-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800"
            >
              Close
            </button>

            {!(event as any).isTimeOff && isOwnerOrParticipantOfEvent() && (
              <div title={!canJoin ? "Joining opens 30 min before start." : ""} className="inline-block">
                <button
                  onClick={() => handleClickJoin(event.id, !canJoin)}
                  disabled={!canJoin}
                  className={`view-event-join inline-flex h-10 items-center justify-center gap-2 rounded-xl border px-5 text-sm font-semibold transition-colors
                    ${canJoin
                      ? "border-[#6D4DB3] bg-[#6D4DB3] text-white hover:border-[#5E3F9E] hover:bg-[#5E3F9E]"
                      : "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400"}
                  `}
                >
                  <Video className="h-4 w-4" /> Join
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Embedded Modals */}
      <ModalMeet isOpen={isEditModalOpen} onClose={() => setEditModalOpen(false)}>
        <EditEvent
          displayTimeZone={displayTimeZone}
          event={event}
          onClose={() => { setEditModalOpen(false); onClose(); }}
          onEventCreated={refreshAllEvents}
        />
      </ModalMeet>

      <ModalMeet isOpen={showRescheduleModal} onClose={() => setShowRescheduleModal(false)}>
        {event && (
          <RescheduleMeetingModal
            displayTimeZone={displayTimeZone}
            handleCloseRescheduleModal={() => setShowRescheduleModal(false)}
            rescheduleModal={true}
            authToken={token}
            selectedMeeting={{
              id: event.id, title: event.title, startTime: event.startTime, endTime: event.endTime,
              description: event.description, ownerUserID: event.ownerUserID, meetingDetails: event.meetingDetails,
              location: event.location,
              attendees: event.attendees || [],
              taskId: event.taskId,
              taskTitle: event.taskTitle || '',
              ownerColor: event.ownerColor,
              ownerName: event.ownerName, online: event.online, isSprout: event.isSprout
            }}
            refreshMeeting={() => { if (refreshAllEvents) refreshAllEvents(); onClose(); }}
          />
        )}
      </ModalMeet>

      <ScopeModal
        isOpen={showDeleteScopeModal}
        onClose={() => setShowDeleteScopeModal(false)}
        onSelectScope={executeDelete}
        action="delete"
      />
    </>
  );
}
